// Trail Thanks — a tiny paw-print button below every walker review.
// Auth-only (guests get the standard sign-in nudge). Self-voting is
// blocked server-side; we also hide the button when we know the viewer
// wrote the review to avoid a wasted round-trip.
import React, { useState } from "react";
import { PawPrint, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { guestNudge } from "../lib/achievementUX";
import { track } from "../lib/telemetry";

export default function HelpfulButton({
  reviewId,
  reviewAuthorId,
  locationId,
  initialCount = 0,
  initialVoted = false,
  currentUser = null,
}) {
  const [count, setCount] = useState(initialCount);
  const [voted, setVoted] = useState(initialVoted);
  const [busy, setBusy] = useState(false);

  const isAuthor = !!currentUser && !!reviewAuthorId && currentUser.user_id === reviewAuthorId;

  const onClick = async (ev) => {
    if (guestNudge(currentUser, ev)) return;
    if (isAuthor || busy) return;
    setBusy(true);
    // Optimistic flip — we roll back on error.
    const prevVoted = voted;
    const prevCount = count;
    setVoted(!prevVoted);
    setCount(prevVoted ? Math.max(0, prevCount - 1) : prevCount + 1);
    try {
      const res = await api.post(`/locations/${locationId}/reviews/${reviewId}/helpful`);
      setCount(res.data.helpful_votes_count);
      setVoted(!!res.data.viewer_has_voted);
      track("review_helpful_vote", {
        review_id: reviewId,
        location_id: locationId,
        voted: !!res.data.viewer_has_voted,
      });
    } catch (err) {
      setVoted(prevVoted);
      setCount(prevCount);
      toast.error(err?.response?.data?.detail || "Couldn't record your thanks. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (isAuthor) {
    // Don't render self-vote UI for the review author.
    return null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`helpful-btn-${reviewId}`}
      data-voted={voted ? "true" : "false"}
      className={
        "mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors " +
        (voted
          ? "bg-brand-primary/10 border-brand-primary/40 text-brand-primary"
          : "bg-brand-surface border-brand-border text-brand-subtle hover:border-brand-primary/40 hover:text-brand-primary")
      }
      aria-pressed={voted}
      title={voted ? "You've thanked this walker" : "Was this review helpful?"}
    >
      {busy ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <PawPrint size={12} className={voted ? "fill-current" : ""} />
      )}
      <span>Helpful</span>
      <span
        data-testid={`helpful-count-${reviewId}`}
        className={
          "tabular-nums " +
          (voted ? "text-brand-primary font-semibold" : "text-brand-subtle")
        }
      >
        {count}
      </span>
    </button>
  );
}
